import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from "react-dom";
import Popup from "reactjs-popup";
import { GoPlus, GoTrash } from "react-icons/go";
import { CgAdd } from "react-icons/cg";
import { FiMinusCircle } from "react-icons/fi";
import AddressInput from './addressInput';
import "./css/CrudModal.css";
import ChartViewer from './ChartViewer';
import { calculateSolarCapacity } from '../../../utils/solarCalculations';
import { UploadRoutes } from '../inputs/UploadRoutes';
import { LoadingSpinner } from '../../general/LoadingSpinner';

/**
 * A unified component for handling CRUD operations (Create, Read, Update, Delete)
 * @param {Object} props
 * @param {string} props.entity - The name of the entity being manipulated (e.g., "Depot", "Route")
 * @param {string} props.mode - The current mode: "add", "edit", or "delete"
 * @param {Array} props.fields - Array of field definitions for add/edit modes
 * @param {Function} props.onSubmit - Function to call when submitting the form
 * @param {Object} props.selectedItems - Array of items (for delete mode) or single item (for edit mode)
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {boolean} props.isOpen - Whether the modal is open (for edit/delete modes)
 * @param {Object} props.initialData - Initial data for the form (for edit mode)
 * @param {boolean} props.dynamicAddRoute - Whether to enable dynamic route/stop addition
 * @param {string} props.buttonText - Custom text for the trigger button
 * @param {string} props.buttonPosition - CSS class for positioning the button
 * @param {boolean} props.hideUpdateButton - Whether to hide the update button for edit mode
 * @param {ReactNode} props.additionalContent - Additional content to display in the modal
 * @param {boolean} props.showUploadRoutes - Whether to render an option for users to upload routes
 * @param {boolean} props.useGoogle - Whether to use Google Maps API for address input
 */
const CrudModal = ({
  entity,
  mode = "add",
  fields = [],
  onSubmit,
  selectedItems = [],
  onClose,
  isOpen = false,
  initialData = null,
  dynamicAddRoute = false,
  buttonText,
  buttonPosition = "",
  hideUpdateButton = false,
  additionalContent = null,
  showUploadRoutes = false,
  useGoogle = true,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(isOpen);
  const [activeTab, setActiveTab] = useState('basic'); // For tabbed layout in edit mode
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const [activeRouteInputMethod, setActiveRouteInputMethod] = useState(
    dynamicAddRoute ? 'manual' : (showUploadRoutes ? 'upload' : null)
  );

  // Initialize form with initialData if provided
  useEffect(() => {
    if (initialData) {
      // Start with the initialData
      const formDataWithDefaults = { ...initialData };

      // Add default values for fields that don't have values
      if (fields) {
        fields.forEach(field => {
          if (field.defaultValue !== undefined &&
            (formDataWithDefaults[field.id] === undefined ||
              formDataWithDefaults[field.id] === null ||
              formDataWithDefaults[field.id] === '')) {
            formDataWithDefaults[field.id] = field.defaultValue;
          }
        });
      }

      // If solar is enabled, calculate max capacity if it's not set
      if (formDataWithDefaults.consider_solar !== false &&
        formDataWithDefaults.solar_area &&
        formDataWithDefaults.solar_type &&
        !formDataWithDefaults.solar_max_capacity) {

        formDataWithDefaults.solar_max_capacity = calculateSolarCapacity(
          formDataWithDefaults.solar_area,
          formDataWithDefaults.solar_type
        );
      }

      setFormData(formDataWithDefaults);
    } else if (fields) {
      // If no initialData but we have fields with defaultValues, initialize form data with those defaults
      const defaultFormData = {};
      fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          defaultFormData[field.id] = field.defaultValue;
        }
      });

      if (Object.keys(defaultFormData).length > 0) {
        setFormData(defaultFormData);
      }
    }
  }, [initialData, fields]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [fieldId]: value
      };

      // Auto-calculate solar capacity when solar area or type changes
      if ((fieldId === 'solar_area' || fieldId === 'solar_type') &&
        newFormData.consider_solar !== false) {

        // Make sure we have both required values
        if (newFormData.solar_area && newFormData.solar_type) {
          newFormData.solar_max_capacity = calculateSolarCapacity(
            newFormData.solar_area,
            newFormData.solar_type
          );
        }
      }

      return newFormData;
    });

    // Clear error when user types
    if (errors[fieldId]) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: null
      }));
    }
  };

  const addStop = () => {
    setFormData((prev) => ({
      ...prev,
      stops: [...(prev.stops || []), ""],
    }));
  };

  const removeStop = (index) => {
    setFormData((prev) => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index),
      idle_times: prev.idle_times ?
        [...prev.idle_times.slice(0, index), ...prev.idle_times.slice(index + 1)] :
        []
    }));
  };

  const validateForm = () => {
    if (mode === "delete") return true;

    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.name} is required`;
      }
      if (field.type === 'number' && field.required && isNaN(parseFloat(formData[field.id]))) {
        newErrors[field.id] = `${field.name} must be a number`;
      }
    });

    // Validate stops and idle times if dynamicAddRoute is enabled
    if (dynamicAddRoute && formData.stops && formData.stops.length > 0) {
      // Check if any stop address is empty
      formData.stops.forEach((stop, index) => {
        if (!stop) {
          newErrors[`stop_${index}`] = `Stop ${index + 1} address is required`;
        }

        // Check idle times for all stops except the last one
        if (index < formData.stops.length - 1) {
          if (!formData.idle_times || !formData.idle_times[index]) {
            newErrors[`idle_time_${index}`] = `Idle time for Stop ${index + 1} is required`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (close) => {
    if (validateForm()) {
      try {
        setIsLoading(true);

        // Call the appropriate action based on mode
        if (mode === "delete") {
          await onSubmit(selectedItems);
        } else {
          await onSubmit(formData);
        }

        setIsLoading(false);

        if (mode === "add") {
          setFormData({}); // Clear form after successful addition
        }

        close();

        if (onClose) {
          onClose();
        }
      } catch (error) {
        console.error('Error processing form:', error);
        setIsLoading(false);
      }
    }
  };

  const handleClose = (close) => {
    close();
    if (onClose) {
      onClose();
    }
  };

  // Add this helper function near the top of the file after imports
  const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Update the renderInput function to handle currency formatting
  const renderInput = (field) => {
    const commonInputClasses = "w-full px-3 py-2 border border-gray-500 rounded-lg focus:ring-indigo-500 focus:border-indigo-500";
    const disabledClasses = "bg-gray-100 cursor-not-allowed";

    if (field.type === 'address') {
      return (
        <div>
          <AddressInput
            onAddressSelect={(address) => handleInputChange(field.id, address)}
            initialValue={formData[field.id] || ''}
            disabled={field.disabled || isLoading}
            useGoogle={useGoogle}
          />
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    if (field.type === 'time') {
      return (
        <div>
          <input
            type="time"
            id={field.id}
            className={`${commonInputClasses} ${shouldGreyOut(field) ? disabledClasses : ''}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            disabled={isLoading}
          />
          <small className="input-helper-text">Enter departure time (24-hour format)</small>
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div>
          <div className="relative w-full">
            <select
              id={field.id}
              name={field.id}
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              disabled={field.disabled || isLoading || shouldGreyOut(field)}
              className={`${commonInputClasses} appearance-none ${shouldGreyOut(field) ? disabledClasses : ''}`}
            >
              {field.options && field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
            </div>
          </div>
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    if (field.type === 'switch') {
      return (
        <div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={!!formData[field.id]}
              onChange={(e) => handleInputChange(field.id, e.target.checked)}
              disabled={isLoading || shouldGreyOut(field)}
            />
            <span className="slider"></span>
          </label>
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <div className="checkbox-container">
          <input
            type="checkbox"
            id={field.id}
            name={field.id}
            checked={!!formData[field.id]}
            onChange={(e) => handleInputChange(field.id, e.target.checked)}
            disabled={isLoading}
          />
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    if (field.type === 'textarea' || field.type === 'json') {
      const isRateOrProfile = ['electricity_buy_rates', 'electricity_sell_rates', 'solar_generation_profile'].includes(field.id);

      return (
        <div>
          {/* <textarea
            id={field.id}
            name={field.id}
            value={field.type === 'json' && typeof formData[field.id] === 'object' 
              ? JSON.stringify(formData[field.id], null, 2) 
              : formData[field.id] || ''}
            onChange={(e) => field.type === 'json' 
              ? handleJsonChange(field.id, e.target.value) 
              : handleInputChange(field.id, e.target.value)}
            rows={field.rows || 4}
            disabled={isLoading}
            className={field.type === 'json' ? "json-editor" : ""}
          /> */}
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}

          {/* Add chart visualization for rate data and solar profile */}
          {isRateOrProfile && formData[field.id] && (
            <div className="chart-preview">
              <ChartViewer data={formData[field.id]} type={field.id} />
            </div>
          )}
        </div>
      );
    }

    // Add special handling for currency type inputs
    if (field.type === 'currency') {
      return (
        <div>
          <input
            type="text"
            id={field.id}
            name={field.id}
            value={formData[field.id] ? formatCurrency(formData[field.id]) : ''}
            onChange={(e) => {
              // Extract numeric value from formatted string
              const numericValue = e.target.value.replace(/[^0-9.-]+/g, '');
              handleInputChange(field.id, numericValue ? parseFloat(numericValue) : '');
            }}
            onBlur={(e) => {
              // Format the value on blur
              if (formData[field.id]) {
                e.target.value = formatCurrency(formData[field.id]);
              }
            }}
            onFocus={(e) => {
              // Show only the number on focus for easier editing
              if (formData[field.id]) {
                e.target.value = formData[field.id];
              }
            }}
            disabled={field.disabled || isLoading || shouldGreyOut(field)}
            className={`${commonInputClasses} ${field.disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder={field.placeholder || '$0'}
          />
          {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
        </div>
      );
    }

    // Default to text/number input
    return (
      <div>
        <input
          type={field.type || 'text'}
          id={field.id}
          name={field.id}
          value={formData[field.id] || field.defaultValue || ''}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
          disabled={field.disabled || isLoading || shouldGreyOut(field)}
          className={`${commonInputClasses} ${field.disabled ? disabledClasses : ''}`}
          placeholder={field.placeholder}
        />
        {errors[field.id] && <div className="text-red-500 text-xs mt-1">{errors[field.id]}</div>}
      </div>
    );
  };

  // Special rendering for circuit information field - updated with editing capability
  const renderCircuitInfoField = (field) => {
    // Get the current item's circuit data
    const circuitData = formData.circuits || [];
    const circuitPowers = formData.circuit_powers || [];
    const circuitDists = formData.circuit_dists || [];

    // Check if we have circuit information
    const hasCircuitInfo = circuitData.length > 0 && circuitPowers.length > 0 && circuitDists.length > 0;

    // Helper function to update circuit data when either capacity or distance changes
    const updateCircuitData = () => {
      const capacity = formData.manual_capacity;
      const distance = formData.manual_distance;

      // Always update what we have, even if incomplete
      if (capacity && capacity > 0) {
        const powerInkW = parseFloat(capacity) * 1000; // Convert MW to kW
        handleInputChange("circuits", ["Manual Circuit"]);
        handleInputChange("circuit_powers", [powerInkW]);
      }

      if (distance && distance > 0) {
        const distanceInMiles = parseFloat(distance);
        handleInputChange("circuits", ["Manual Circuit"]);
        handleInputChange("circuit_dists", [distanceInMiles]);
      }
    };

    // Function to handle adding a new circuit
    const addCircuit = () => {
      const updatedCircuits = [...(formData.circuits || []), "New Circuit"];
      const updatedPowers = [...(formData.circuit_powers || []), 0];
      const updatedDists = [...(formData.circuit_dists || []), 0];

      handleInputChange("circuits", updatedCircuits);
      handleInputChange("circuit_powers", updatedPowers);
      handleInputChange("circuit_dists", updatedDists);
    };

    // Function to handle removing a circuit
    const removeCircuit = (index) => {
      const updatedCircuits = [...(formData.circuits || [])];
      const updatedPowers = [...(formData.circuit_powers || [])];
      const updatedDists = [...(formData.circuit_dists || [])];

      updatedCircuits.splice(index, 1);
      updatedPowers.splice(index, 1);
      updatedDists.splice(index, 1);

      handleInputChange("circuits", updatedCircuits);
      handleInputChange("circuit_powers", updatedPowers);
      handleInputChange("circuit_dists", updatedDists);
    };

    // Function to update a circuit's name
    const updateCircuitName = (index, newName) => {
      const updatedCircuits = [...(formData.circuits || [])];
      updatedCircuits[index] = newName;
      handleInputChange("circuits", updatedCircuits);
    };

    // Function to update a circuit's power
    const updateCircuitPower = (index, newPower) => {
      const updatedPowers = [...(formData.circuit_powers || [])];
      updatedPowers[index] = parseFloat(newPower) * 1000; // Convert MW to kW for storage
      handleInputChange("circuit_powers", updatedPowers);
    };

    // Function to update a circuit's distance
    const updateCircuitDistance = (index, newDistance) => {
      const updatedDists = [...(formData.circuit_dists || [])];
      updatedDists[index] = parseFloat(newDistance);
      handleInputChange("circuit_dists", updatedDists);
    };

    if (!hasCircuitInfo) {
      // Show the manual input option when no circuit info exists
      return (
        <div>
          <div className="manual-circuit-container" style={{ marginTop: "10px" }}>
            <div className="manual-circuit-header" style={{ marginBottom: "10px" }}>
              <em style={{ color: "#666", fontSize: "14px" }}>
                No circuit information is available for this location currently. While we recommend consulting your utility for accurate circuit data, you can proceed by setting estimated capacities for scenario studies without utility input. This allows you to explore different capacity scenarios and analyze their impacts.
              </em>
            </div>

            <div className="manual-circuit-input" style={{ marginTop: "15px" }}>
              <label className="popup-input-group-label" style={{ marginBottom: "5px" }}>
                Available Hosting Capacity (MW)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="popup-input"
                value={formData.manual_capacity || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Create a synthetic circuit with the manually specified capacity
                  handleInputChange("manual_capacity", value);

                  // Update the circuit data if both capacity and distance are valid
                  updateCircuitData();
                }}
                placeholder="e.g., 2.5"
              />
              <small className="input-helper-text">
                Specify the estimated available circuit capacity in megawatts
              </small>
            </div>
            <div className="manual-circuit-input" style={{ marginTop: "15px" }}>
              <label className="popup-input-group-label" style={{ marginBottom: "5px" }}>
                Distance to the Circuit (miles)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="popup-input"
                value={formData.manual_distance || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Store the distance value
                  handleInputChange("manual_distance", value);

                  // Update the circuit data if both capacity and distance are valid
                  updateCircuitData();
                }}
                placeholder="e.g., 0.5"
              />
              <small className="input-helper-text">
                Specify the estimated distance to the circuit in miles
              </small>
            </div>

            {/* <button 
              type="button" 
              className="add-button-custom text-only"
              onClick={addCircuit}
              disabled={isLoading}
              style={{ marginTop: "15px" }}
            >
              Add New Circuit
            </button> */}
          </div>
        </div>
      );
    }

    // Updated table display with editing capabilities
    return (
      <div>
        <div className="embedded-table-container">
          <table className="embedded-table">
            <thead>
              <tr>
                <th>Circuit</th>
                <th>Capacity (MW)</th>
                <th>Distance (miles)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {circuitData.map((circuit, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="text"
                      value={circuit}
                      onChange={(e) => updateCircuitName(idx, e.target.value)}
                      className="embedded-input"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={(circuitPowers[idx] !== undefined ? (circuitPowers[idx] / 1000).toFixed(2) : "0")}
                      onChange={(e) => updateCircuitPower(idx, e.target.value)}
                      className="embedded-input"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={circuitDists[idx] !== undefined ? circuitDists[idx] : "0"}
                      onChange={(e) => updateCircuitDistance(idx, e.target.value)}
                      className="embedded-input"
                    />
                  </td>
                  <td>
                    <button
                      className="remove-btn"
                      onClick={() => removeCircuit(idx)}
                      disabled={isLoading}
                    >
                      <FiMinusCircle size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="add-button-custom text-only"
          onClick={addCircuit}
          disabled={isLoading}
          style={{ marginTop: "10px" }}
        >
          Add New Circuit
        </button>
        <small className="input-helper-text">
          Circuit information last updated: June 13, 2025
        </small>
      </div>
    );
  };

  // Special rendering for charger types field - updated to use the embedded table classes
  const renderChargerTypeField = (field) => {
    const fieldKey = field.id;
    let chargerTypes = formData[fieldKey] || {};

    // If chargerTypes is a string, try to parse it
    if (typeof chargerTypes === 'string') {
      try {
        chargerTypes = JSON.parse(chargerTypes);
      } catch (e) {
        console.error("Could not parse charger types:", e);
        chargerTypes = {};
      }
    }

    // Convert to array for easier rendering
    const chargerTypesArray = Object.keys(chargerTypes).map(key => ({
      id: key,
      ...chargerTypes[key]
    })).sort((a, b) => parseFloat(a.power_limit) - parseFloat(b.power_limit));

    const handleTypeToggle = (id) => {
      const updatedTypes = { ...chargerTypes };
      updatedTypes[id] = {
        ...updatedTypes[id],
        considered: !updatedTypes[id].considered
      };

      // Update the formData
      setFormData({
        ...formData,
        [fieldKey]: updatedTypes
      });
    };

    const handleTypeChange = (id, field, value) => {
      const updatedTypes = { ...chargerTypes };

      // Ensure the charger type exists
      if (!updatedTypes[id]) {
        updatedTypes[id] = {};
      }

      // Update the specific field
      updatedTypes[id][field] = field === 'considered'
        ? Boolean(value)
        : (field === 'cost' || field === 'power_limit')
          ? (field === 'cost' && typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : parseFloat(value))
          : value;

      // Update the formData
      setFormData({
        ...formData,
        [fieldKey]: updatedTypes
      });
    };

    // Function to add new charger type
    const addChargerType = () => {
      const nextId = chargerTypesArray.length > 0
        ? Math.max(...chargerTypesArray.map(t => parseInt(t.id))) + 1
        : 0;

      const updatedTypes = { ...chargerTypes };
      updatedTypes[nextId.toString()] = {
        power_limit: 0,
        cost: 0.0,
        considered: true
      };

      setFormData({
        ...formData,
        [fieldKey]: updatedTypes
      });
    };

    // Function to remove a charger type
    const removeChargerType = (id) => {
      const updatedTypes = { ...chargerTypes };
      delete updatedTypes[id];

      setFormData({
        ...formData,
        [fieldKey]: updatedTypes
      });
    };

    return (
      <div>
        <div className="embedded-table-container">
          <table className="embedded-table">
            <thead>
              <tr>
                <th>Consider</th>
                <th>Power Limit (kW)</th>
                <th>Cost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {chargerTypesArray.length > 0 ? (
                chargerTypesArray.map((type) => (
                  <tr key={type.id} className={type.considered ? 'considered' : 'not-considered'}>
                    <td className="checkbox-cell">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={type.considered || false}
                          onChange={() => handleTypeToggle(type.id)}
                          disabled={isLoading}
                          id={`charger-${type.id}`}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={type.power_limit || ''}
                        onChange={(e) => handleTypeChange(type.id, 'power_limit', e.target.value)}
                        disabled={isLoading}
                        className="embedded-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={type.cost ? formatCurrency(type.cost) : ''}
                        onChange={(e) => handleTypeChange(type.id, 'cost', e.target.value)}
                        onBlur={(e) => {
                          // Format on blur
                          if (chargerTypes[type.id]?.cost) {
                            e.target.value = formatCurrency(chargerTypes[type.id].cost);
                          }
                        }}
                        onFocus={(e) => {
                          // Show raw value on focus
                          if (chargerTypes[type.id]?.cost) {
                            e.target.value = chargerTypes[type.id].cost;
                          }
                        }}
                        disabled={isLoading}
                        className="embedded-input"
                      />
                    </td>
                    <td>
                      <button
                        className="remove-btn"
                        onClick={() => removeChargerType(type.id)}
                        disabled={isLoading}
                      >
                        <FiMinusCircle size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-chargers">No charger types defined</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="add-button-custom text-only"
          onClick={addChargerType}
          disabled={isLoading}
          style={{ marginTop: "10px" }}
        >
          Add New Charger Type
        </button>
      </div>
    );
  };

  const renderTruckClassField = (field) => {
    return (
      <div>
        <select className="popup-input" value={formData[field.id] || ''} onChange={(e) => handleInputChange(field.id, e.target.value)}>
          {field.options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
    );
  };

  // Function to handle special field types - update with the new circuit info field type
  const renderSpecialField = (field) => {
    switch (field.specialType) {
      case 'charger_types':
        return renderChargerTypeField(field);
      case 'circuit_info':
        return renderCircuitInfoField(field);
      case 'truck_class':
        return renderTruckClassField(field);
      default:
        return renderInput(field);
    }
  };

  // Group fields by category for tabbed navigation (edit mode)
  const getFieldsByCategory = () => {
    const categories = {};

    fields.forEach(field => {
      const category = field.category || 'basic';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(field);
    });

    return categories;
  };

  // Function to get item's name
  const getItemName = (item) => {
    return item.name || item.raw_route_name || item.route_name || item.depot_name || `${item.make} ${item.model}` || "Unnamed";
  };

  // Button appearance based on mode
  const getButtonAppearance = () => {
    switch (mode) {
      case "delete":
        return {
          text: buttonText || `Delete ${entity}`,
          icon: <GoTrash size={19} />,
          color: '#ff4444',
          hoverColor: '#ff0000',
          disabled: !selectedItems?.length
        };
      case "edit":
        return {
          text: buttonText || `Edit ${entity}`,
          icon: null,
          color: '#5E5ADE',
          hoverColor: '#4D48C0',
          disabled: false
        };
      case "add":
      default:
        return {
          text: buttonText || `Add ${entity}`,
          icon: <GoPlus size={19} />,
          color: '#5E5ADE',
          hoverColor: '#4D48C0',
          disabled: false
        };
    }
  };

  // Get button appearance
  const buttonAppearance = getButtonAppearance();

  // Modal title based on mode
  const getModalTitle = () => {
    // Special case for charging stations
    if (mode === "edit" && entity === "Charging Station") {
      return `View ${entity}`;
    }

    switch (mode) {
      case "delete":
        return `Delete ${entity}${selectedItems?.length > 1 ? 's' : ''}`;
      case "edit":
        return `Edit ${entity}`;
      case "add":
      default:
        return `Add ${entity}`;
    }
  };

  // Action button text based on mode
  const getActionButtonText = () => {
    if (isLoading) {
      switch (mode) {
        case "delete": return "Deleting...";
        case "edit": return "Updating...";
        case "add": return "Adding...";
        default: return "Processing...";
      }
    }

    switch (mode) {
      case "delete": return "Delete";
      case "edit": return "Update";
      case "add": return "Add";
      default: return "Submit";
    }
  };

  // Render tab navigation for edit mode
  const renderTabNavigation = () => {
    const categories = getFieldsByCategory();
    const categoryNames = Object.keys(categories);

    if (categoryNames.length <= 1) return null;

    return (
      <div className="modal-tabs">
        {categoryNames.map(category => (
          <button
            key={category}
            className={`tab-button ${activeTab === category ? 'active' : ''}`}
            onClick={() => setActiveTab(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>
    );
  };

  // Replace the current tooltip implementation with this improved version
  const renderTooltipIcon = (fieldId, tooltipText) => {
    return (
      <div className="tooltip-container">
        <span
          className="tooltip-icon"
          onMouseEnter={() => {
            tooltipTimeoutRef.current = setTimeout(() => {
              setActiveTooltip(fieldId);
            }, 200); // Small delay to prevent flashing on quick mouse movements
          }}
          onMouseLeave={() => {
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
            }
            setActiveTooltip(null);
          }}
        >
          <i className="info-icon">i</i>
        </span>
        {activeTooltip === fieldId && (
          <div className="tooltip-content">
            {tooltipText}
          </div>
        )}
      </div>
    );
  };

  // Add a function to determine if a field should be greyed out
  const shouldGreyOut = (field) => {
    // Grey out disabled fields
    if (field.disabled) {
      return true;
    }

    // Grey out solar fields if consider_solar is false
    if (field.category === 'solar' && field.id !== 'consider_solar') {
      return formData.consider_solar === false;
    }
    // Grey out battery fields if consider_battery is false
    if (field.category === 'battery' && field.id !== 'consider_battery') {
      return formData.consider_battery === false;
    }
    return false;
  };

  // Render modal content based on mode
  const renderModalContent = () => {
    switch (mode) {
      case "delete":
        return (
          <div className="p-6">
            <p className="text-base text-gray-700">Are you sure you want to delete the following {entity.toLowerCase()}{selectedItems?.length > 1 ? 's' : ''}?</p>
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              {selectedItems?.map((item, index) => (
                <p key={index}>
                  <span className="font-semibold">{index + 1}.</span> {getItemName(item)}
                </p>
              ))}
            </div>
          </div>
        );

      case "edit":
      case "add":
      default:
        if (mode === "edit" && fields.length > 10) {
          // Use tabbed layout for complex edit forms
          const categories = getFieldsByCategory();

          return (
            <div className="p-0">
              {renderTabNavigation()}

              <div className="p-6">
                {categories[activeTab]?.map(field => (
                  <div
                    className={`mb-4 ${shouldGreyOut(field) ? 'opacity-50 pointer-events-none' : ''}`}
                    key={field.id}
                  >
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      {field.name}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                      {field.tooltip && renderTooltipIcon(field.id, field.tooltip)}
                    </div>
                    {field.specialType ? renderSpecialField(field) : renderInput(field)}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Simple form layout for add mode or simple edit forms
        return (
          <div className="p-6">
            <div className="space-y-4">
              {activeRouteInputMethod !== 'upload' && fields.map((field) => (
                <div
                  className={`mb-4 ${shouldGreyOut(field) ? 'opacity-50 pointer-events-none' : ''}`}
                  key={field.id}
                >
                  <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    {field.name}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                    {field.tooltip && renderTooltipIcon(field.id, field.tooltip)}
                  </div>
                  {field.specialType ? renderSpecialField(field) : renderInput(field)}
                </div>
              ))}

              {/* This section will only appear if both manual adding and uploading are possible */}
              {dynamicAddRoute && showUploadRoutes && (
                <div className="flex items-center gap-4 mb-4 border-b border-gray-200 pb-3">
                  <button
                    type="button"
                    onClick={() => setActiveRouteInputMethod('manual')}
                    className={`py-2 px-3 text-sm font-nunito font-medium border-b-2 transition-colors duration-150 ${activeRouteInputMethod === 'manual'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                      }`}
                  >
                    Enter Single Route
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveRouteInputMethod('upload')}
                    className={`py-2 px-3 text-sm font-nunito font-medium border-b-2 transition-colors duration-150 ${activeRouteInputMethod === 'upload'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                      }`}
                  >
                    Batch Upload Routes
                  </button>
                </div>
              )}

              {/* Conditionally render manual route input section */}
              {dynamicAddRoute && activeRouteInputMethod === 'manual' && (
                <div className="mt-4 space-y-4 min-h-[20rem]">
                  <h3 className="text-base font-semibold font-nunito text-gray-800 border-b border-gray-200 pb-2">Trip Itinerary</h3>

                  {/* Origin with departure time side by side */}
                  <div className="mt-4 space-y-4">
                    <div key="origin" className='flex items-start gap-4 relative z-10'>
                      <div className="w-24 flex-shrink-0 pt-2 text-right">
                        <p className='text-sm font-medium font-nunito text-gray-700'>Origin</p>
                      </div>
                      <div className="flex-grow relative">
                        <AddressInput
                          onAddressSelect={(address) => handleInputChange('origin', address)}
                          initialValue={formData.origin || ''}
                        />
                        {errors['origin'] && <div className="text-xs text-red-600 mt-1">{errors['origin']}</div>}
                        <small className="text-xs font-nunito text-gray-500 mt-1">Enter the location where you start your operation for the day</small>
                      </div>
                      <div className="w-40 flex-shrink-0">
                        <input
                          className='w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
                          type="time"
                          placeholder='Departure time'
                          value={formData.start_time || ''}
                          onChange={(e) => handleInputChange('start_time', e.target.value)}
                        />
                        <small className="text-xs font-nunito text-gray-500 mt-1">Departure time</small>
                        {errors['start_time'] && <div className="text-xs text-red-600 mt-1">{errors['start_time']}</div>}
                      </div>
                      <div className="w-10 h-10 flex-shrink-0"></div> {/* Placeholder */}
                    </div>

                    {/* Additional stops with idle times */}
                    {formData.stops?.map((stop, index) => (
                      <div key={index} className='flex items-start gap-4 relative z-10'>
                        <div className="w-24 flex-shrink-0 pt-2 text-right">
                          <p className='text-sm font-medium font-nunito text-gray-700'>Stop {index + 1}</p>
                        </div>
                        <div className="flex-grow relative">
                          <AddressInput
                            onAddressSelect={(address) => {
                              const updatedStops = [...(formData.stops || [])];
                              updatedStops[index] = address;
                              setFormData((prev) => ({ ...prev, stops: updatedStops }));
                            }}
                            initialValue={stop || ''}
                          />
                          {errors[`stop_${index}`] && <div className="text-xs text-red-600 mt-1">{errors[`stop_${index}`]}</div>}
                          {index === formData.stops.length - 1 && (
                            <small className="text-xs font-nunito text-gray-500 mt-1">This is your final destination. Add more stops below if needed.</small>
                          )}
                        </div>
                        <div className="w-40 flex-shrink-0">
                          {index < formData.stops.length - 1 ? (
                            <>
                              <div className="relative w-full">
                                <input
                                  className='w-full p-2 pr-12 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-nunito [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                                  type="number"
                                  placeholder='Idle time'
                                  value={formData.idle_times?.[index] || ''}
                                  onChange={(e) => {
                                    const updatedTimes = [...(formData.idle_times || [])];
                                    updatedTimes[index] = e.target.value;
                                    setFormData((prev) => ({ ...prev, idle_times: updatedTimes }));
                                  }}
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-sm font-nunito text-gray-500">min</span>
                                </div>
                              </div>
                              {errors[`idle_time_${index}`] && <div className="text-xs text-red-600 mt-1">{errors[`idle_time_${index}`]}</div>}
                            </>
                          ) : (
                            <div className="h-10"></div>
                          )}
                        </div>
                        <button
                          className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100 transition-colors"
                          type="button"
                          onClick={() => removeStop(index)}
                          aria-label={`Remove stop ${index + 1}`}
                        >
                          <FiMinusCircle size={22} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {(!formData.stops || formData.stops.length <= 6) && (
                    <div className="flex justify-end">
                      <button
                        className="flex items-center gap-2 text-sm font-medium font-nunito text-blue-600 hover:text-blue-800 transition-colors"
                        type="button"
                        onClick={addStop}
                      >
                        <CgAdd size={18} /> Add a stop
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Conditionally render upload routes section */}
              {showUploadRoutes && activeRouteInputMethod === 'upload' && (
                <div className="mt-4">
                  <UploadRoutes />
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  // If this is an embedded modal (edit or delete with isOpen prop)
  if (mode === "edit" && isOpen !== undefined) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative flex flex-col w-full max-h-[90vh] max-w-4xl bg-white border border-gray-200 rounded-xl shadow-lg">
          {isLoading && (
            <LoadingSpinner />
          )}
          <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-gray-200">
            <h3 className="m-0 text-xl font-semibold text-gray-800 font-nunito">{getModalTitle()}</h3>
            <button type="button" className="text-[28px] font-light text-gray-500 hover:text-gray-800" onClick={onClose}>&times;</button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {renderModalContent()}
          </div>

          {!hideUpdateButton && (
            <div className="flex justify-center w-full px-6 py-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
              <button
                className={`btn-primary font-nunito`}
                onClick={() => handleSubmit(onClose)}
                disabled={isLoading}
              >
                {buttonAppearance.icon} {getActionButtonText()} {mode !== "delete" ? entity : ""}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // For add mode, delete mode or triggered modals
  return (
    <div className={buttonPosition}>
      <Popup
        trigger={
          <button
            className={`inline-flex items-center bg-blue-700 gap-2 py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${buttonAppearance.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={buttonAppearance.disabled}
          >
            {buttonAppearance.icon} {buttonAppearance.text}
          </button>
        }
        modal
        nested
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        closeOnDocumentClick={false}
        overlayStyle={{ background: 'rgba(0,0,0,0.5)' }}
      >
        {close => (
          <div className="flex items-center justify-center min-h-screen font-nunito">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 sm:mx-0">
              {isLoading && (
                <LoadingSpinner />
              )}
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">{getModalTitle()}</h3>
                <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={() => handleClose(close)}>&times;</button>
              </div>

              {renderModalContent()}

              {!hideUpdateButton && (
                <div className="px-4 py-3 bg-gray-50 text-center sm:px-6 rounded-b-lg">
                  <button
                    className={`btn-primary font-nunito`}
                    onClick={() => handleSubmit(close)}
                    disabled={isLoading}
                  >
                    {buttonAppearance.icon} {getActionButtonText()}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
};

export default CrudModal; 